import {
  Dashboardv99990101Api,
  dashboardv99990101Client
} from '@sequencehq/api/dashboard/v99990101'
import { useMutation } from '@sequencehq/api'
import { Currency } from '@sequencehq/api/utils'
import {
  InputSelectControlField,
  SimpleModal,
  Box,
  Link
} from '@sequencehq/core-components'
import {
  GreyGrey70,
  IndigoIndigo60,
  Primary13Regular
} from '@sequencehq/core-components'
import { format } from '@sequencehq/utils/dates'
import { useNotifications } from '@sequencehq/core-components'
import { useState } from 'react'

type TargetFormats = Dashboardv99990101Api.PostJournalReport.CSVTarget

/**
 * Note: as of the initial 'Hippo(cratic)' release, the only supported target formats
 * are Xero and QuickBooks.
 */
const CSV_FORMAT_OPTIONS: Array<{ label: string; value: TargetFormats }> = [
  {
    label: 'Xero',
    value: 'XERO'
  },
  {
    label: 'Sequence',
    value: 'SEQUENCE'
  },
  {
    label: 'QuickBooks',
    value: 'QUICKBOOKS'
  }
]

const INITIAL_STATE = 'SEQUENCE'

export const ExportCSVModal = (props: {
  trigger: React.ReactElement
  from: Date
  to: Date
  currency: Currency
  customerIds?: string[]
  productIds?: string[]
  invoiceIds?: string[]
  creditNoteIds?: string[]
}) => {
  const [csvFormat, setCsvFormat] = useState<TargetFormats>(INITIAL_STATE)
  const { displayNotification } = useNotifications()

  const postJournalReportMutation = useMutation(
    dashboardv99990101Client.postJournalReport
  )

  const entityFilters = {
    ...(props.customerIds ? { customerIds: props.customerIds } : {}),
    ...(props.productIds ? { productIds: props.productIds } : {}),
    ...(props.invoiceIds ? { invoiceIds: props.invoiceIds } : {}),
    ...(props.creditNoteIds ? { creditNoteIds: props.creditNoteIds } : {})
  }

  const onSubmit = async () => {
    try {
      await postJournalReportMutation.mutateAsync({
        from: format(props.from, 'yyyy-MM-dd'),
        to: format(props.to, 'yyyy-MM-dd'),
        currency: props.currency,
        target: csvFormat,
        fileFormat: 'CSV' as const,
        includeFutureDatedJournals: false,
        // Xero exports collapse to one summary journal per (month, customer,
        // product, direction, account code). Other targets must omit
        // aggregation — backend rejects MONTHLY_BY_CUSTOMER_PRODUCT with a
        // non-Xero target.
        ...(csvFormat === 'XERO'
          ? { aggregation: 'MONTHLY_BY_CUSTOMER_PRODUCT' as const }
          : {}),
        ...entityFilters
      })
      displayNotification(
        "We're generating your report. You'll receive an email when it's ready.",
        { type: 'success' }
      )
      return true
    } catch (e) {
      displayNotification('Failed to export CSV', {
        type: 'error'
      })
      return false
    }
  }

  return (
    <SimpleModal
      minHeight="160px"
      title="Export Journal Report CSV"
      trigger={props.trigger}
      cancelButtonText="Cancel"
      submitButtonText="Export"
      submittingButtonText="Export"
      data-testid="revrec.modal.exportCSV"
      onSubmit={onSubmit}
    >
      <InputSelectControlField
        data-testid="select.csvFormat"
        description={
          <Box {...Primary13Regular} color={GreyGrey70}>
            We'll send a download link to your email. This may take a few
            minutes.{' '}
            <Link
              to="https://docs.sequencehq.com/revenue-recognition/overview"
              target="_blank"
              color={IndigoIndigo60}
            >
              Learn more
            </Link>
          </Box>
        }
        label="CSV format"
        initialValue={csvFormat}
        onChange={newValue => setCsvFormat(newValue as TargetFormats)}
        options={CSV_FORMAT_OPTIONS}
      />
    </SimpleModal>
  )
}
